<template lang="pug">
  div
    .mb-6
      h1.display-1.mb-2 Delete Confirmation Dialog Component
      p.text--secondary Specialized dialog component for delete confirmations with predefined content and action buttons. Provides consistent UX for destructive actions.

    // Basic Examples
    .component-section.mb-8#basic-examples
      h3.mb-4.cursor-pointer(@click="navigateToSection('basic-examples')") Basic Examples
      .variations-grid
        .variation-item.mb-6(v-for="variation in basicVariations" :key="variation.name" :id="variation.id")
          .variation-header.mb-3
            h4.cursor-pointer(@click="navigateToSection(variation.id)") {{ variation.name }}
            p.text--secondary.caption {{ variation.description }}

          .variation-preview.mb-4.pa-4.border.rounded
            .mb-2
              span.font-weight-medium {{ variation.label }}
              small.text--secondary.ml-2(v-if="variation.hint") {{ variation.hint }}

            .d-flex.align-center.gap-2
              yz-btn(:label="variation.buttonLabel" color="danger" @click="openDialog(variation.id)")

            yz-delete-confirmation-dialog(v-model="dialogs[variation.id]" v-bind="variation.props" @confirmed="handleConfirmed(variation.id)" @canceled="handleCanceled(variation.id)")
              yz-card.mt-3.pa-3(v-if="variation.id === 'with-slot'" color="red-100" outlined border-color="danger-500")
                .d-flex.align-center.mb-2
                  yz-icon.mr-2(icon="mdi-alert-circle" color="danger-500")
                  span.yz-medium-body-sm.text-danger-700 This will also delete:
                ul.mb-0.ml-4.yz-regular-body-sm.text-danger-600
                  li All your personal data
                  li Account settings and preferences
                  li Saved files and documents

          .variation-code
            .code-header.d-flex.align-center.mb-2
              yz-btn(icon-only size="md" :icon="copiedCode === variation.code ? 'mdi-check' : 'mdi-content-copy'" :icon-color="copiedCode === variation.code ? 'success' : ''" @click="copyCode(variation.code)")
              span.font-weight-medium.ml-2 Code

            pre.code-block.pa-3.rounded.text-caption(style="background-color: #f5f5f5; overflow-x: auto;")
              code {{ variation.code }}

    // API Documentation
    .component-section.mb-8#api
      h3.mb-4.cursor-pointer(@click="navigateToSection('api')") API

      h4.mb-3 Props
      .props-table.mb-6
        v-simple-table
          template(v-slot:default)
            thead
              tr
                th Prop
                th Type
                th Default
                th Description
            tbody
              tr(v-for="prop in componentProps" :key="prop.name")
                td.font-weight-medium.text-no-wrap {{ prop.name }}
                td.text--secondary.text-no-wrap {{ prop.type }}
                td.text--secondary.text-no-wrap {{ prop.default }}
                td.single-line {{ prop.description }}

      h4.mb-3 Events
      .events-table.mb-6
        v-simple-table
          template(v-slot:default)
            thead
              tr
                th Event
                th Description
            tbody
              tr(v-for="event in componentEvents" :key="event.name")
                td.font-weight-medium.text-no-wrap {{ event.name }}
                td.single-line {{ event.description }}

      h4.mb-3 Slots
      .slots-table.mb-6
        v-simple-table
          template(v-slot:default)
            thead
              tr
                th Slot
                th Description
            tbody
              tr(v-for="slot in componentSlots" :key="slot.name")
                td.font-weight-medium.text-no-wrap {{ slot.name }}
                td.single-line {{ slot.description }}

      h4.mb-3 Implementation Notes
      .notes-section
        v-alert(type="info" outlined)
          ul.mb-0
            li
              strong Dynamic Content: 
              | Uses 'item' prop to customize the confirmation message dynamically
            li
              strong Destructive Action: 
              | Uses danger-colored "Confirm" button to make the destructive nature clear
            li
              strong Event Handling: 
              | Emits specific events (confirmed, canceled) and supports v-model for dialog state
            li
              strong Extensibility: 
              | Default slot allows additional content below the description text
            li
              strong Flexible Sizing: 
              | maxWidth prop allows customization of dialog width with automatic unit conversion
            li
              strong Consistent UX: 
              | Follows established dialog patterns while being specialized for delete confirmations

    // Data Display
    .component-section.mb-8#data-display(v-if="Object.keys(eventLog).length > 0")
      h3.mb-4.cursor-pointer(@click="navigateToSection('data-display')") Live Event Log
      p.text--secondary.mb-3 Real-time display of dialog events as you interact with the examples above.

      .data-display.pa-4.border.rounded(style="background-color: #f8f9fa; max-height: 300px; overflow-y: auto;")
        .event-log(v-for="(events, dialogId) in eventLog" :key="dialogId")
          h5.mb-2 {{ getDialogTitle(dialogId) }}
          .event-item.mb-1(v-for="(event, index) in events" :key="index" :class="getEventClass(event.type)")
            .d-flex.justify-space-between.align-center
              span.font-weight-medium {{ event.type }}
              small.text--secondary {{ event.timestamp }}
</template>

<script>
export default {
  data() {
    return {
      copiedCode: null,
      dialogs: {},
      eventLog: {}
    }
  },
  computed: {
    basicVariations() {
      return [
        {
          name: 'Basic Delete Confirmation',
          id: 'basic-delete',
          description: 'Standard delete confirmation with default item and description',
          label: 'Basic Delete Confirmation',
          hint: 'Default item deletion',
          buttonLabel: 'Delete Item',
          props: {},
          code: `yz-btn(label="Delete Item" color="danger" @click="openDialog('basic-delete')")

yz-delete-confirmation-dialog(
  v-model="dialogs['basic-delete']"
  @confirmed="handleConfirmed('basic-delete')"
  @canceled="handleCanceled('basic-delete')"
)`
        },
        {
          name: 'Custom Item and Description',
          id: 'custom-content',
          description: 'Delete confirmation with custom item type and description text',
          label: 'Custom Content',
          hint: 'Custom item and description',
          buttonLabel: 'Delete User',
          props: {
            item: 'user',
            description: 'This user will be permanently removed from your system. All their data and access will be revoked immediately.'
          },
          code: `yz-btn(label="Delete User" color="danger" @click="openDialog('custom-content')")

yz-delete-confirmation-dialog(
  v-model="dialogs['custom-content']"
  item="user"
  description="This user will be permanently removed from your system. All their data and access will be revoked immediately."
  @confirmed="handleConfirmed('custom-content')"
  @canceled="handleCanceled('custom-content')"
)`
        },
        {
          name: 'Custom Dialog Size',
          id: 'custom-size',
          description: 'Delete confirmation with custom dialog width',
          label: 'Custom Dialog Size',
          hint: 'Wider dialog',
          buttonLabel: 'Delete Project',
          props: {
            item: 'project',
            description: 'This will permanently delete the project and all associated files, configurations, and history.',
            maxWidth: '600px'
          },
          code: `yz-btn(label="Delete Project" color="danger" @click="openDialog('custom-size')")

yz-delete-confirmation-dialog(
  v-model="dialogs['custom-size']"
  item="project"
  description="This will permanently delete the project and all associated files, configurations, and history."
  max-width="600px"
  @confirmed="handleConfirmed('custom-size')"
  @canceled="handleCanceled('custom-size')"
)`
        },
        {
          name: 'With Additional Content',
          id: 'with-slot',
          description: 'Delete confirmation with additional content in the default slot',
          label: 'With Additional Content',
          hint: 'Extra content in slot',
          buttonLabel: 'Delete Account',
          props: {
            item: 'account',
            description: 'Your account and all associated data will be permanently deleted.'
          },
          code: `yz-btn(label="Delete Account" color="danger" @click="openDialog('with-slot')")

yz-delete-confirmation-dialog(
  v-model="dialogs['with-slot']"
  item="account"
  description="Your account and all associated data will be permanently deleted."
  @confirmed="handleConfirmed('with-slot')"
  @canceled="handleCanceled('with-slot')"
)
  yz-card.mt-3.pa-3(color="red-100" outlined border-color="danger-500")
    .d-flex.align-center.mb-2
      yz-icon.mr-2(icon="mdi-alert-circle" color="danger-500")
      span.yz-medium-body-sm.text-danger-700 This will also delete:
     ul.mb-0.ml-4.yz-regular-body-sm.text-danger-600
      li All your personal data
      li Account settings and preferences
      li Saved files and documents`
        }
      ]
    },
    componentProps() {
      return [
        {
          name: 'v-model (value)',
          type: 'Boolean',
          default: 'false',
          description: 'Controls the visibility of the dialog'
        },
        {
          name: 'item',
          type: 'String',
          default: '"item"',
          description: 'Type of item being deleted, used in the confirmation message'
        },
        {
          name: 'description',
          type: 'String',
          default: '"This action will be done immediately and can\'t be undone. Click \\"Confirm\\" to proceed"',
          description: 'Description text displayed below the main confirmation message'
        },
        {
          name: 'max-width',
          type: 'String | Number',
          default: '"500px"',
          description: 'Maximum width of the dialog. Numbers are converted to px units'
        }
      ]
    },
    componentEvents() {
      return [
        {
          name: 'input',
          description: 'Emitted when the dialog visibility changes (v-model)'
        },
        {
          name: 'confirmed',
          description: 'Emitted when the user clicks the Confirm button'
        },
        {
          name: 'canceled',
          description: 'Emitted when the user clicks the Cancel button'
        }
      ]
    },
    componentSlots() {
      return [
        {
          name: 'default',
          description: 'Additional content displayed below the description text'
        }
      ]
    }
  },
  mounted() {
    this.$nextTick(() => {
      this.scrollToSection()
    })
  },
  watch: {
    '$route.hash': {
      handler() {
        this.scrollToSection()
      },
      immediate: false
    }
  },
  methods: {
    navigateToSection(sectionId) {
      this.$router.push({
        name: this.$route.name,
        hash: `#${sectionId}`,
        query: this.$route.query
      })
    },
    scrollToSection() {
      const sectionIdWithHash = this.$route.hash
      const sectionId = sectionIdWithHash.replace('#', '')
      if (sectionId) {
        this.$nextTick(() => {
          const element = document.getElementById(sectionId)
          if (element) {
            element.scrollIntoView({
              behavior: 'smooth',
              block: 'start'
            })
          }
        })
      }
    },
    async copyCode(code) {
      try {
        await navigator.clipboard.writeText(code)
        this.copiedCode = code
        setTimeout(() => {
          this.copiedCode = null
        }, 2000)
      } catch (err) {
        console.error('Failed to copy code:', err)
      }
    },
    openDialog(dialogId) {
      this.$set(this.dialogs, dialogId, true)
      this.logEvent(dialogId, 'opened')
    },
    handleConfirmed(dialogId) {
      this.logEvent(dialogId, 'confirmed')
    },
    handleCanceled(dialogId) {
      this.logEvent(dialogId, 'canceled')
    },
    logEvent(dialogId, eventType) {
      if (!this.eventLog[dialogId]) {
        this.$set(this.eventLog, dialogId, [])
      }
      const timestamp = new Date().toLocaleTimeString()
      this.eventLog[dialogId].unshift({
        type: eventType,
        timestamp: timestamp
      })
      
      if (this.eventLog[dialogId].length > 5) {
        this.eventLog[dialogId] = this.eventLog[dialogId].slice(0, 5)
      }
    },
    getDialogTitle(dialogId) {
      const variation = this.basicVariations.find(v => v.id === dialogId)
      return variation ? variation.name : dialogId
    },
    getEventClass(eventType) {
      switch (eventType) {
        case 'confirmed':
          return 'text-success'
        case 'canceled':
          return 'text-warning'
        case 'opened':
          return 'text-info'
        default:
          return ''
      }
    }
  }
}
</script>

<style scoped>
.variations-grid {
  max-width: 100%;
}

.variation-item {
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  padding: 20px;
  background-color: #ffffff;
}

.variation-preview {
  background-color: #fafafa;
  border: 1px dashed #d0d0d0 !important;
}

.code-block {
  background-color: #f8f9fa !important;
  border: 1px solid #e9ecef;
  font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
  line-height: 1.5;
}

.code-header {
  border-bottom: 1px solid #e0e0e0;
  padding-bottom: 8px;
}

.component-section {
  background-color: #ffffff;
  border-radius: 8px;
  border: 1px solid #e0e0e0;
  padding: 24px;
}

.props-table .v-data-table {
  border: 1px solid #e0e0e0;
}

.single-line {
  white-space: normal;
  word-wrap: break-word;
  max-width: 400px;
}

.text-no-wrap {
  white-space: nowrap;
}

.cursor-pointer {
  cursor: pointer;
  transition: color 0.2s ease;
}

.cursor-pointer:hover {
  color: #1976d2;
}

.gap-2 {
  gap: 8px;
}

.data-display {
  font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
  font-size: 12px;
}

.event-item {
  padding: 4px 8px;
  border-radius: 4px;
  background-color: #ffffff;
  border: 1px solid #e0e0e0;
}
</style>